// src/components/SkillsPanel.js
import React, { useState } from 'react';
import styles from './SkillsPanel.module.css';
import { skillData as allSkillDefinitions } from '../data/data';

const SkillsPanel = ({ playerData }) => {
  const [activeCategory, setActiveCategory] = useState('Core');
  const [expandedSkills, setExpandedSkills] = useState(new Set());
  
  const skills = playerData.skills || {};

  const getXpToNextLevel = (level) => {
    return (level * 100) + 100;
  };

  const getSkillLevelColor = (level) => {
    if (level >= 50) return '#ff6b35'; // Master (orange-red)
    if (level >= 25) return '#f7931e'; // Expert (orange)  
    if (level >= 10) return '#4caf50'; // Proficient (green)
    if (level >= 5) return '#2196f3';  // Intermediate (blue)
    return '#9e9e9e'; // Novice (gray)
  };

  const categorizedSkills = {
    Core: [],
    Combat: [],
    Secondary: [],
    Utility: [],
    Other: [],
  };

  for (const skillId in skills) {
    const skillDef = allSkillDefinitions[skillId];
    const playerSkill = skills[skillId];

    if (skillDef && playerSkill) {
      const category = skillDef.category || 'Other';
      if (categorizedSkills[category]) {
        categorizedSkills[category].push({
          ...skillDef,
          ...playerSkill,
        });
      } else {
        categorizedSkills.Other.push({
          ...skillDef,
          ...playerSkill,
        });
      }
    }
  }

  // Filter out empty categories and get available categories
  const availableCategories = Object.keys(categorizedSkills).filter(
    category => categorizedSkills[category].length > 0
  );

  // Set first available category as active if current active category is empty
  React.useEffect(() => {
    if (availableCategories.length > 0 && !availableCategories.includes(activeCategory)) {
      setActiveCategory(availableCategories[0]);
    }
  }, [availableCategories, activeCategory]);

  const toggleSkillDetails = (skillId) => {
    const newExpanded = new Set(expandedSkills);
    if (newExpanded.has(skillId)) {
      newExpanded.delete(skillId);
    } else {
      newExpanded.add(skillId);
    }
    setExpandedSkills(newExpanded);
  };

  const renderSkillItem = (skill) => {
    const xpForNext = getXpToNextLevel(skill.level);
    const progressPercent = skill.xp > 0 ? (skill.xp / xpForNext) * 100 : 0;
    const isExpanded = expandedSkills.has(skill.id);
    const levelColor = getSkillLevelColor(skill.level);

    return (
      <div key={skill.id} className={styles.skillRow}>
        <div 
          className={styles.skillMain}
          onClick={() => toggleSkillDetails(skill.id)}
        >
          <div className={styles.skillInfo}>
            <div className={styles.skillHeader}>
              <span className={styles.skillName}>{skill.name}</span>
              <span 
                className={styles.skillLevel}
                style={{ backgroundColor: levelColor }}
              >
                {skill.level}
              </span>
            </div>
            <div className={styles.skillProgressMini}>
              <div className={styles.progressBarMini}>
                <div
                  className={styles.progressFillMini}
                  style={{ 
                    width: `${Math.min(progressPercent, 100)}%`,
                    backgroundColor: levelColor
                  }}
                />
              </div>
              <span className={styles.xpTextMini}>
                {skill.xp}/{xpForNext} XP
              </span>
            </div>
          </div>
          <div className={styles.expandIcon}>
            {isExpanded ? '▼' : '▶'}
          </div>
        </div>
        
        {isExpanded && (
          <div className={styles.skillDetails}>
            <p className={styles.skillDescription}>{skill.description}</p>
            {skill.benefits && (
              <div className={styles.skillBenefits}>
                <strong>Current Benefit:</strong> {skill.benefits.current}
              </div>
            )}
            <div className={styles.skillProgressDetailed}>
              <div className={styles.progressBarDetailed}>
                <div
                  className={styles.progressFillDetailed}
                  style={{ 
                    width: `${Math.min(progressPercent, 100)}%`,
                    backgroundColor: levelColor
                  }}
                />
              </div>
              <div className={styles.xpDetails}>
                <span>Level {skill.level}</span>
                <span>{Math.round(progressPercent)}% to next level</span>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.skillsPanelContainer}>
      <div className={styles.skillsPanelHeader}>
        <h2 className={styles.mainTitle}>Skills & Proficiency</h2>
        <p className={styles.skillsSubtitle}>
          Tap skills for details • {Object.keys(skills).length} total skills
        </p>
      </div>

      {/* Category Tabs */}
      <div className={styles.categoryTabs}>
        {availableCategories.map(category => (
          <button
            key={category}
            className={`${styles.categoryTab} ${activeCategory === category ? styles.activeTab : ''}`}
            onClick={() => setActiveCategory(category)}
          >
            {category}
            <span className={styles.categoryCount}>
              {categorizedSkills[category].length}
            </span>
          </button>
        ))}
      </div>

      {/* Skills List */}
      <div className={styles.skillsList}>
        {categorizedSkills[activeCategory]?.map(renderSkillItem)}
      </div>

      {/* Summary Stats */}
      <div className={styles.skillsSummary}>
        <div className={styles.summaryItem}>
          <span className={styles.summaryLabel}>Total Level:</span>
          <span className={styles.summaryValue}>
            {Object.values(skills).reduce((sum, skill) => sum + skill.level, 0)}
          </span>
        </div>
        <div className={styles.summaryItem}>
          <span className={styles.summaryLabel}>Average Level:</span>
          <span className={styles.summaryValue}>
            {Object.keys(skills).length > 0 
              ? Math.round(Object.values(skills).reduce((sum, skill) => sum + skill.level, 0) / Object.keys(skills).length)
              : 0
            }
          </span>
        </div>
      </div>
    </div>
  );
};

export default SkillsPanel;